/**
 * Passordregler — hold i takt med backend/utils/passwordPolicy.js
 */
export const MIN_LENGTH = 8;
export const MAX_LENGTH = 64;

const normalize = (s) => (s == null ? '' : String(s)).trim().toLowerCase();

export const hasLowerLetter = (p) => {
	const str = String(p ?? '');
	for (const ch of str) {
		if (ch !== ch.toUpperCase() && ch === ch.toLowerCase()) {
			return true;
		}
	}
	return false;
};

export const hasUpperLetter = (p) => {
	const str = String(p ?? '');
	for (const ch of str) {
		if (ch !== ch.toLowerCase() && ch === ch.toUpperCase()) {
			return true;
		}
	}
	return false;
};

export const hasSpecialChar = (p) => /[^\p{L}\p{N}]/u.test(String(p ?? ''));

export const collectShapeErrors = (password) => {
	const p = password == null ? '' : String(password);
	const errors = [];
	if (p.length < MIN_LENGTH) {
		errors.push('Passordet må ha minst 8 tegn.');
	}
	if (p.length > MAX_LENGTH) {
		errors.push('Passordet kan ikke ha mer enn 64 tegn.');
	}
	if (!hasLowerLetter(p)) {
		errors.push('Passordet må inneholde minst én liten bokstav.');
	}
	if (!hasUpperLetter(p)) {
		errors.push('Passordet må inneholde minst én stor bokstav.');
	}
	if (!hasSpecialChar(p)) {
		errors.push(
			'Passordet må inneholde minst ett tegn som ikke er bokstav eller siffer (f.eks. ! ? #).'
		);
	}
	return errors;
};

export const getIdentityErrors = (password, { username, email }) => {
	const p = password == null ? '' : String(password);
	const n = normalize(p);
	if (!n) {
		return [];
	}
	const out = [];
	if (username && n === normalize(username)) {
		out.push('Passordet kan ikke være det samme som brukernavnet.');
	}
	if (email && n === normalize(email)) {
		out.push('Passordet kan ikke være det samme som e-postadressen.');
	}
	return out;
};

/**
 * @param {string} password
 * @param {{ username?: string, email?: string, skipIdentity?: boolean }} [opts]
 * @returns {{ ok: boolean, errors: string[] }}
 */
export const validatePassword = (password, opts = {}) => {
	const { username, email, skipIdentity } = opts;
	const shape = collectShapeErrors(password);
	if (shape.length) {
		return { ok: false, errors: shape };
	}
	if (!skipIdentity) {
		const id = getIdentityErrors(password, { username, email });
		if (id.length) {
			return { ok: false, errors: id };
		}
	}
	return { ok: true, errors: [] };
};

/** 0–1 for visuell styrkeindikator (andel oppfylte krav) */
export const passwordStrengthRatio = (password, { username, email } = {}) => {
	const p = String(password ?? '');
	const hasCtx = Boolean(username || email);
	const checks = [
		p.length >= MIN_LENGTH && p.length <= MAX_LENGTH,
		hasLowerLetter(p),
		hasUpperLetter(p),
		hasSpecialChar(p),
	];
	if (hasCtx) {
		checks.push(
			!p || getIdentityErrors(p, { username, email }).length === 0
		);
	}
	const met = checks.filter(Boolean).length;
	return met / checks.length;
};

export const getPasswordCriteria = (password, { username, email } = {}) => {
	const p = String(password ?? '');
	return {
		lengthOk: p.length >= MIN_LENGTH && p.length <= MAX_LENGTH,
		lower: hasLowerLetter(p),
		upper: hasUpperLetter(p),
		special: hasSpecialChar(p),
		notIdentity:
			!p || getIdentityErrors(p, { username, email }).length === 0,
	};
};
